import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTree, MatTreeModule } from '@angular/material/tree';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { TenantSettingsEnum } from '@models/tenant';
import { ModuleIconComponent } from '@shared/components/module-icon/module-icon.component';
import { CipoFormModule } from '@shared/modules';
import { UtilsService } from '@shared/services';
import { UserStore } from '@signal-store/index';
import { TenantStore } from '@signal-store/tenant.store';
import { FooterComponent } from '@system/footer/footer.component';

import { MenuItem, RouteCodeEnum } from '@models/menu';
import { getAncestorFoldersInTree, isMenuFolder } from './common/menu-item.utils';
import { MenuFlyoutFitDirective } from './common/menu-flyout-fit.directive';
import { MenuFlyoutComponent } from './menu-flyout/menu-flyout.component';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CipoFormModule,
    MatTreeModule,
    MatTooltipModule,
    MatMenuModule,
    ModuleIconComponent,
    FooterComponent,
    MenuFlyoutComponent,
    MenuFlyoutFitDirective,
  ],
})
export class MenuComponent {
  matTree = viewChild(MatTree);
  searchInput = viewChild<ElementRef<HTMLInputElement>>('inputsearch');

  searchControl = new FormControl('');
  searchValue = toSignal(this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()), {
    initialValue: '',
  });

  userStore = inject(UserStore);
  tenantStore = inject(TenantStore);
  router = inject(Router);
  dialog = inject(MatDialog);
  utilsService = inject(UtilsService);

  menuItems = input<MenuItem[]>([]);
  menuItemClick = output<MenuItem>();

  private _wasCollapsed = false;
  private _lastExpandedActiveId: number | null = null;

  processedMenuItems = computed(() => {
    let items = this.menuItems();
    if (this.userStore.theme.mode() === 'dark') {
      items = items.map(item => (item.color ? { ...item, color: this.utilsService.invertHex(item.color) } : item));
    }

    const itemsCopy = this.deepCloneMenuItems(items);
    this.setParent(itemsCopy);
    this.setParentChildren(itemsCopy);
    return itemsCopy;
  });

  treeData = computed(() => {
    const items = this.processedMenuItems();
    const search = this.searchValue();
    return this.filterMenuItems(search, items);
  });

  isCollapsed = computed(() => this.userStore.menu.isCollapsed());

  activeMenuId = computed(() => this.userStore.menu.activeMenuItem()?.id);

  menuFolderAutoCollapse = computed(
    () => this.tenantStore.getSetting(TenantSettingsEnum.menu_folder_auto_collapse)?.calculatedValue as boolean,
  );

  menuAutoClose = computed(
    () => this.tenantStore.getSetting(TenantSettingsEnum.menu_auto_close)?.calculatedValue as boolean,
  );

  constructor() {
    effect(() => {
      const data = this.treeData();
      const search = this.searchValue();

      if (search) {
        setTimeout(() => this.expandAllFiltered(data));
      }
    });

    effect(() => {
      const collapsed = this.isCollapsed();
      if (!collapsed && this._wasCollapsed) {
        setTimeout(() => {
          this.searchInput()?.nativeElement?.focus();
        });
      }
      if (collapsed) {
        this.matTree()?.collapseAll();
      }
      this._wasCollapsed = collapsed;
    });

    afterRenderEffect(() => {
      const activeItem = this.userStore.menu.activeMenuItem();
      const collapsed = this.isCollapsed();
      const search = this.searchValue();
      const hasMenuData = this.menuItems().length > 0;

      if (!activeItem || collapsed || search || !hasMenuData) {
        if (collapsed || search) {
          this._lastExpandedActiveId = null;
        }
        return;
      }

      this.expandToActiveItem();
    });
  }

  childrenAccessor = (node: MenuItem): MenuItem[] => node.children ?? [];

  hasChild = (_: number, node: MenuItem): boolean => isMenuFolder(node);

  isExpanded(node: MenuItem): boolean {
    const tree = this.matTree();
    return tree ? tree.isExpanded(node) : false;
  }

  folderIconId(menu: MenuItem, expanded = false): number {
    return menu.iconId ?? (expanded ? 7 : 6);
  }

  onFolderNodeActivate(menu: MenuItem, flyoutTrigger: MatMenuTrigger): void {
    if (this.isCollapsed()) {
      flyoutTrigger.openMenu();
    } else {
      this.menuFolderClick(menu);
    }
  }

  clearSearch(): void {
    this.searchControl.reset('');
  }

  filterMenuItems(searchValue: string, menuItems: MenuItem[]): MenuItem[] {
    if (!searchValue) {
      return (menuItems ?? [])
        .filter(m => !m.hidden && (!this.hasChild(null, m) || (m.children && m.children.length > 0)))
        .map(m => (this.hasChild(null, m) ? { ...m, children: this.filterMenuItems(null, m.children) } : m));
    }
    const items: MenuItem[] = [];
    for (const menuItem of menuItems ?? []) {
      if (menuItem.hidden) continue;
      if (this.hasChild(null, menuItem)) {
        const children = this.filterMenuItems(searchValue, menuItem.children);
        if (children.length > 0) items.push({ ...menuItem, children });
      } else if (
        menuItem.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        menuItem.code?.toLowerCase().includes(searchValue.toLowerCase())
      ) {
        items.push({ ...menuItem });
      }
    }
    return items;
  }

  private expandAllFiltered(items: MenuItem[]): void {
    const tree = this.matTree();
    if (!tree) return;

    for (const item of items ?? []) {
      if (this.hasChild(null, item)) {
        tree.expand(item);
        this.expandAllFiltered(item.children);
      }
    }
  }

  private expandToActiveItem(): void {
    const tree = this.matTree();
    const activeId = this.userStore.menu.activeMenuItem()?.id;
    if (!tree || activeId == null) {
      return;
    }

    const folders = getAncestorFoldersInTree(this.treeData(), activeId);
    if (this._lastExpandedActiveId === activeId && folders.every(folder => tree.isExpanded(folder))) {
      return;
    }

    for (const folder of folders) {
      if (!tree.isExpanded(folder)) {
        tree.expand(folder);
      }
    }
    this._lastExpandedActiveId = activeId;
  }

  menuFolderClick(menuItem: MenuItem): void {
    if (this.isCollapsed()) return;

    const tree = this.matTree();
    if (!tree) return;

    const willExpand = !tree.isExpanded(menuItem);
    tree.toggle(menuItem);

    if (this.menuFolderAutoCollapse()) {
      for (const sibling of this.getSiblings(menuItem)) {
        tree.collapse(sibling);
      }

      const parent = menuItem.parent;
      if (parent?.children) {
        for (const child of parent.children) {
          if (child !== menuItem && tree.isExpanded(child)) {
            tree.collapse(child);
          }
        }
      }

      if (willExpand && menuItem.children) {
        for (const child of menuItem.children) {
          tree.collapse(child);
        }
      }

      if (!willExpand && menuItem.children) {
        for (const child of menuItem.children) {
          tree.collapse(child);
        }
      }
    }
  }

  onMenuItemClick(menuItem: MenuItem): void {
    this.menuItemClick.emit(menuItem);
    this.closeAllDialogs();

    if (this.userStore.isSmallScreen()) {
      this.userStore.collapseMenu(true);
    }

    if (menuItem.openInNewTab) {
      window.open(menuItem.url, '_blank');
    } else {
      this.userStore.setActiveMenuItem(menuItem);
      this.router.navigateByUrl(this.getNavigationUrlPath(menuItem));

      if (this.menuAutoClose()) {
        this.userStore.collapseMenu(true);
      }
    }
  }

  private getNavigationUrlPath(menuItem: MenuItem): string {
    if (menuItem.code !== RouteCodeEnum.FileContractManager) {
      return menuItem.urlPath;
    }

    const entityInstanceId = this.userStore.contextEntityInstanceId();
    return entityInstanceId ? menuItem.urlPath.replace(/\/\d+(?=\?|$)/, `/${entityInstanceId}`) : menuItem.urlPath;
  }

  private getSiblings(menuItem: MenuItem): MenuItem[] {
    if (menuItem.parent) {
      return (menuItem.parent.children ?? []).filter(c => c.id !== menuItem.id);
    }
    return (this.treeData() ?? []).filter(c => c.id !== menuItem.id);
  }

  private deepCloneMenuItems(items: MenuItem[]): MenuItem[] {
    return items.map(item => ({
      ...item,
      children: item.children ? this.deepCloneMenuItems(item.children) : undefined,
    }));
  }

  private setParent(menuItems: MenuItem[], parent?: MenuItem, level?: number): void {
    if (!menuItems || menuItems.length === 0) return;
    let cleanParent: MenuItem = null;
    if (parent) {
      const { children, ...props } = parent;
      cleanParent = props;
    }
    for (const menuItem of menuItems) {
      if (cleanParent) menuItem.parent = cleanParent;
      menuItem.level = (level || 0) + 1;
      this.setParent(menuItem.children, menuItem, (level || 0) + 1);
    }
  }

  private setParentChildren(menuItems: MenuItem[], parent?: MenuItem): void {
    if (!menuItems) return;
    for (const item of menuItems) {
      item.parent = parent ?? null;
      this.setParentChildren(item.children, item);
    }
  }

  private closeAllDialogs() {
    for (const dialog of this.dialog.openDialogs) {
      dialog.close();
    }
  }
}
